
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAppointmentStatusUpdate = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appointmentId, status }: { appointmentId: string; status: string }) => {
      console.log('Updating appointment status:', { appointmentId, status });
      
      // Vérifier l'authentification
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('Utilisateur non authentifié');
      }

      console.log('Authenticated user:', user.id);

      // Vérifier d'abord que le rendez-vous existe et appartient au professionnel
      const { data: appointment, error: fetchError } = await supabase
        .from("professional_appointments")
        .select("id, professional_id, status, patient_id")
        .eq("id", appointmentId)
        .single();

      if (fetchError) {
        console.error('Error fetching appointment:', fetchError);
        throw new Error(`Erreur lors de la récupération du rendez-vous: ${fetchError.message}`);
      }

      if (!appointment) {
        throw new Error('Rendez-vous introuvable');
      }

      console.log('Found appointment:', appointment);

      if (appointment.professional_id !== user.id) {
        throw new Error('Vous n\'avez pas l\'autorisation de modifier ce rendez-vous');
      }

      if (appointment.status !== 'pending') {
        throw new Error("Cette demande a déjà été mise à jour.");
      }

      // Valider le statut
      const validStatuses = ['pending', 'confirmed', 'canceled', 'completed'];
      if (!validStatuses.includes(status)) {
        throw new Error(`Statut invalide: ${status}`);
      }

      // Effectuer la mise à jour
      const { data, error: updateError } = await supabase
        .from("professional_appointments")
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq("id", appointmentId)
        .eq("professional_id", user.id) // Double vérification pour la sécurité
        .select()
        .single();

      if (updateError) {
        console.error('Error updating appointment:', updateError);
        throw new Error(`Erreur lors de la mise à jour: ${updateError.message}`);
      }

      console.log('Successfully updated appointment:', data);
      return data;
    },
    onSuccess: (data, variables) => {
      console.log('Update successful, invalidating queries');
      
      // Invalider toutes les requêtes liées aux rendez-vous
      queryClient.invalidateQueries({ queryKey: ["professional-appointments"] });
      
      const statusText = variables.status === 'confirmed' ? 'confirmé' : 
                        variables.status === 'canceled' ? 'annulé' : variables.status;
      
      toast({
        title: "Succès",
        description: `Le rendez-vous a été ${statusText} avec succès`,
      });
    },
    onError: (error: Error) => {
      console.error("Detailed error updating appointment status:", error);
      
      toast({
        title: "Information",
        description: error.message || "Impossible de mettre à jour le statut du rendez-vous",
        variant: "destructive",
      });
    },
  });
};
